import React from 'react';
import type { ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { makeStyles } from 'tss-react/mui';

import AnimatedHoverIcon from 'components/AnimatedHoverIcon';
import { getDrawerSx } from 'components/Drawers/drawerStyles';
import QIllustration from 'components/QIllustration';
import { tracker } from 'app-components/utils/core';
import { taskAnimations, taskIcons } from './animations';

export type DrawerStep = 'form' | 'processing' | 'success';

export type NextTaskCard = {
  taskId: string;
  label: string;
  description?: string;
  onClick: () => void;
};

type SetupTaskDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  step: DrawerStep;
  children: ReactNode;
  saveLabel?: string;
  onSave: () => void;
  saveDisabled?: boolean;
  dismissLabel?: string;
  onDismiss?: () => void;
  successMessage: string;
  successDescription?: string;
  nextTasks?: NextTaskCard[];
  doneLabel?: string;
  onDone?: () => void;
  addAnotherLabel?: string;
  onAddAnother?: () => void;
  analyticsSection?: string;
  errorMessage?: string | null;
};

const DRAWER_WIDTH = 520;

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: '100%',
    [theme.breakpoints.up('sm')]: { width: DRAWER_WIDTH },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(4),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  successCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: theme.spacing(4),
    textAlign: 'center',
    gap: theme.spacing(3),
  },
}));

const NextTaskCardItem = React.memo(function NextTaskCardItem({ taskId, label, description, onClick }: NextTaskCard) {
  const theme = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <Paper
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      elevation={0}
      sx={{
        borderRadius: 2,
        boxShadow: '0px 0px 0px 1px rgba(0,0,0,0.06), 0px 5px 22px 0px rgba(0,0,0,0.04)',
        p: 2,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        textAlign: 'left',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <AnimatedHoverIcon
        staticIcon={taskIcons[taskId]}
        animationData={taskAnimations[taskId]}
        color={theme.palette.primary.main}
        backgroundColor={theme.palette.primary.light}
        isHovered={isHovered}
      />
      <Stack>
        <Typography variant='paragraphMedium'>{label}</Typography>
        {description && (
          <Typography variant='captionRegular' color='text.secondary'>
            {description}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
});

const SetupTaskDrawer = React.memo(function SetupTaskDrawer({
  open,
  onClose,
  title,
  step,
  children,
  saveLabel,
  onSave,
  saveDisabled,
  dismissLabel,
  onDismiss,
  successMessage,
  successDescription,
  nextTasks,
  doneLabel,
  onDone,
  addAnotherLabel,
  onAddAnother,
  analyticsSection,
  errorMessage,
}: SetupTaskDrawerProps) {
  const { classes } = useStyles();

  const handleDismiss = React.useCallback(() => {
    if (analyticsSection && !onDismiss) {
      tracker.track('businessSetupClick', { section: `${analyticsSection}-later` });
    }
    (onDismiss ?? onClose)();
  }, [analyticsSection, onDismiss, onClose]);

  const handleDone = React.useCallback(() => {
    (onDone ?? onClose)();
  }, [onDone, onClose]);

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
      classes={{ paper: classes.paper }}
      sx={getDrawerSx()}
    >
      <Box className={classes.header}>
        <Typography variant='titleMedium'>{title}</Typography>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {step === 'form' && (
        <Box className={classes.content}>
          {errorMessage && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {children}
        </Box>
      )}

      {step === 'processing' && (
        <Box className={classes.successCenter}>
          <CircularProgress size={64} />
        </Box>
      )}

      {step === 'success' && (
        <Box className={classes.successCenter}>
          <Stack alignItems='center' gap={1} width='100%'>
            <QIllustration name='successCheck' size='lg' />
            <Typography variant='titleMedium'>{successMessage}</Typography>
            {onAddAnother && addAnotherLabel && (
              <Link component='button' variant='paragraphRegular' onClick={onAddAnother}>
                {addAnotherLabel}
              </Link>
            )}
            {successDescription && (
              <Typography variant='bodyRegular' color='text.secondary'>
                {successDescription}
              </Typography>
            )}
          </Stack>
          {nextTasks && nextTasks.length > 0 && <Divider sx={{ alignSelf: 'stretch' }} />}
          {nextTasks?.map((task, index) => (
            <Box key={index} px={6} sx={{ alignSelf: 'stretch' }}>
              <NextTaskCardItem {...task} />
            </Box>
          ))}
        </Box>
      )}

      {step === 'form' && (
        <Box className={classes.footer}>
          <Button variant='outlined' onClick={handleDismiss}>
            {dismissLabel ?? "I'll do it later"}
          </Button>
          <Button variant='contained' onClick={onSave} disabled={saveDisabled}>
            {saveLabel ?? 'Save'}
          </Button>
        </Box>
      )}

      {step === 'success' && (
        <Box className={classes.footer}>
          <Button variant='contained' onClick={handleDone}>
            {doneLabel ?? 'Done'}
          </Button>
        </Box>
      )}
    </Drawer>
  );
});

export default SetupTaskDrawer;
